// src\admin\FormEditor\components\CollapsibleSection.jsx
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

const CollapsibleSection = ({
  id,
  title,
  description,
  icon: Icon,
  bgColor = "bg-white",
  borderColor = "border-gray-200",
  children,
  summary = null,
  defaultCollapsed = true,
  onChange = null,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isDragging, setIsDragging] = useState(false);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(`form-editor-${id}-collapsed`);
    if (saved !== null) {
      setIsCollapsed(JSON.parse(saved));
    }
  }, [id]);

  // Save collapsed state to localStorage and trigger onChange
  const handleToggle = (newState) => {
    if (isDragging) return; // Don't toggle if dragging

    setIsCollapsed(newState);
    localStorage.setItem(
      `form-editor-${id}-collapsed`,
      JSON.stringify(newState)
    );
    if (onChange) {
      onChange(newState);
    }
  };

  // Set up drag detection
  useEffect(() => {
    const handleDragStart = () => setIsDragging(true);
    const handleDragEnd = () => {
      // Add a small delay to prevent immediate click handling
      setTimeout(() => setIsDragging(false), 100);
    };

    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);
    // For Framer Motion's drag events
    document.addEventListener("mousedown", (e) => {
      if (e.target.closest(".cursor-grab")) {
        setIsDragging(true);
      }
    });
    document.addEventListener("mouseup", () => {
      setTimeout(() => setIsDragging(false), 100);
    });

    return () => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("mousedown", handleDragStart);
      document.removeEventListener("mouseup", handleDragEnd);
    };
  }, []);

  return (
    <div
      className={`${bgColor} rounded-lg border ${borderColor} overflow-hidden`}
    >
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none hover:bg-opacity-50 transition-colors"
        onClick={() => handleToggle(!isCollapsed)}
      >
        <div className="flex-1 flex items-center gap-2">
          <ChevronRight
            size={18}
            className={`transform transition-transform ${
              isCollapsed ? "" : "rotate-90"
            }`}
          />
          <div className="flex items-center gap-3 flex-1">
            {Icon && <Icon size={20} className="text-gray-500" />}
            <div className="flex-1">
              <h2 className="font-medium text-gray-900">{title}</h2>
              {isCollapsed && description && (
                <p className="text-sm text-gray-600 mt-0.5">{description}</p>
              )}
              {isCollapsed && summary}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 ml-4">
          <span className="text-sm text-gray-500 inline-block w-20 text-right">
            {isCollapsed ? "Click to edit" : "Editing"}
          </span>
        </div>
      </div>

      {!isCollapsed && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t"
        >
          <div className="p-4">{children}</div>
        </motion.div>
      )}
    </div>
  );
};

export { CollapsibleSection };

